import { fieldConfig } from "@autoform/zod";
import { z } from "zod";

import { Media } from "@/components/forms/schemas/media";
import { geodataNestedFormSchema } from "@/pages/geodata/schemas/geodata.nested.form";
import { VENUE_SECTION_OPTIONS } from "@/pages/venues/schemas/venue";

export const basicVenueSchema = z.object({
  geodata: geodataNestedFormSchema,
  name: z.string().superRefine(
    fieldConfig({
      description: "Venue display name. This will be publicly visible.",
    }),
  ),
  type: z
    .string()
    .superRefine(
      fieldConfig({
        description: "Venue type. Fetched from Google Places API.",
      }),
    )
    .nullish(),
  sections: z
    .array(z.enum(VENUE_SECTION_OPTIONS))
    .default(["Music"])
    .superRefine(
      fieldConfig({
        description:
          "Sections this venue belongs to. Controls which app maps it appears on (e.g. music venues map vs cinemas map).",
      }),
    ),
  address: z.string().superRefine(
    fieldConfig({
      description: "Venue address. Fetched from Google Places API.",
    }),
  ),
  // The Places string is the picker's hint; the picked area's id is what is sent.
  area: z
    .string()
    .superRefine(
      fieldConfig({
        fieldType: "area",
        label: "Area",
        description:
          "Venue area. Google Places suggests one; pick the city's own.",
        customData: { hintFromText: true },
      }),
    )
    .nullish(),
  areaId: z
    .string()
    .superRefine(fieldConfig({ fieldType: "hidden" }))
    .nullish(),
  timezone: z.string().superRefine(
    fieldConfig({
      description: "Venue location timezone.",
    }),
  ),
  phoneNumber: z
    .string()
    .nullish()
    .superRefine(
      fieldConfig({
        description: "Venue's phone number. This will be publicly visible.",
      }),
    ),
  mapsUrl: z
    .string()
    .url()
    .optional()
    .superRefine(
      fieldConfig({
        description: "Venue's google maps URL. This will be publicly visible.",
      }),
    ),
  geolocation: z.object({
    latitude: z.coerce.number(),
    longitude: z.coerce.number(),
  }),
  place: z.any().superRefine(
    fieldConfig({
      inputProps: {
        hidden: true,
      },
    }),
  ),
});

export const mediaVenueSchema = z.object({
  id: z.string().superRefine(fieldConfig({ inputProps: { disabled: true } })),
  hide: z.boolean().default(false),
  media: Media.superRefine(
    fieldConfig({
      fieldType: "media",
      customData: {
        collection: "Venues",
      },
    }),
  ),
});

export const venueAIStepSchema = z.object({
  instagramPageUrl: z.string().nullish(),
  instagramScreenshot: z.string().nullish(),
});

export const createVenueFormSchema = z
  .object({ id: z.string() })
  .merge(basicVenueSchema)
  .merge(venueAIStepSchema)
  .merge(mediaVenueSchema);

export type VenueBasicType = z.infer<typeof basicVenueSchema>;
export type VenueMediaType = z.infer<typeof mediaVenueSchema>;
export type VenueAIEnrichmentStepType = z.infer<typeof venueAIStepSchema>;
export type CreateVenueFormType = z.infer<typeof createVenueFormSchema>;
